import { Suspense, useContext, useEffect, useState } from "react";
import { getCompanyId } from "../../../common/services/company.api";
import { getOperationalManagement } from "../../../common/services/operational-management";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import { VEHICLE_STATUS } from "../../../types/vehicle.type";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import { AuthContext } from "../../../common/components/auth/authContext/AuthContext";
import {
  IDashoardOperationalManagement,
  IOperationalManagement,
} from "../../../types/operational-management";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { getCompanySigla } from "../../../common/helpers/company.helpers";
import ModalAddNotesOperationalManagement from "./modals/ModalAddNotesOperationalManagement";
import { FaInfoCircle } from "react-icons/fa";
import ModalViewNotesOperationalManagement from "./modals/ModalViewNotesOperationalManagement";
import ModalChangeStatusVehicle from "@/modules/vehicles/components/modals/ChangeStatusVehicle";

import { Popover } from "flowbite-react";
import { useSearchParams } from "react-router-dom";
import ModalInfoDaysAtBase from "@/modules/vehicles/components/modals/InfoDaysAtBase";
import ModalCreatePurchaseRequisition from "@/modules/purchase-requisition/components/Modal/ModalCreatePurchaseRequisition";
import { useCompanyStore } from "@/common/states/store/Company";
import { savePublickLink } from "@/common/services/vehicles.api";

const EMPTY_ACTION = { eventName: "", data: undefined };

const DEADLINE_STATUS = {
  [VEHICLE_STATUS.INSURANCE]: 2,
  [VEHICLE_STATUS.UNDER_PREPARATION]: 10,
  [VEHICLE_STATUS.IN_REVISION]: 10,
  [VEHICLE_STATUS.IN_REPAIR]: 10,
  [VEHICLE_STATUS.AVAILABLE]: 16,
  [VEHICLE_STATUS.IMPOUNDED]: 10,
  [VEHICLE_STATUS.THEFT_ROBBERY]: 10,
  [VEHICLE_STATUS.MISAPPROPRIATION]: 60,
  [VEHICLE_STATUS.IN_TRANSIT]: 3,
  [VEHICLE_STATUS.PREPARATION_FOR_SALE]: 15,
  [VEHICLE_STATUS.TRIAGE]: 10,
  [VEHICLE_STATUS.TRIGGER_OPENING]: 30,
};

// eslint-disable-next-line react-refresh/only-export-components
export const STATUS_LABELS = {
  [VEHICLE_STATUS.AVAILABLE]: "Disponível",
  [VEHICLE_STATUS.MISSING_DATA]: "Dados incompletos",
  [VEHICLE_STATUS.RENTED]: "Locado",
  [VEHICLE_STATUS.INACTIVE]: "Inativo",
  [VEHICLE_STATUS.WAITING_PAYMENT]: "Aguardando pagamento",
  [VEHICLE_STATUS.SELLED]: "Veículo vendido",
  [VEHICLE_STATUS.UNDER_PREPARATION]: "Em preparação",
  [VEHICLE_STATUS.UNDER_REVIEW]: "Revisão Programada",
  [VEHICLE_STATUS.IN_REPAIR]: "Em manutenção",
  [VEHICLE_STATUS.IN_REVISION]: "Reparo estético",
  [VEHICLE_STATUS.INSURANCE]: "Abertura de acionamento",
  [VEHICLE_STATUS.TRIGGER_OPENING]: "Proteção acionada",
  [VEHICLE_STATUS.IMPOUNDED]: "Apreendido",
  [VEHICLE_STATUS.THIRD_PARTY_INSURANCE]: "Seguro de Terceiros",
  [VEHICLE_STATUS.PREPARATION_FOR_SALE]: "Motos para Venda",
  [VEHICLE_STATUS.IN_TRANSIT]: "Em trânsito",
  [VEHICLE_STATUS.THEFT_ROBBERY]: "Furto/Roubo",
  [VEHICLE_STATUS.MISAPPROPRIATION]: "Apropriação indébita",
  [VEHICLE_STATUS.VEHICLE_ON_RESERVED]: "Moto reserva",
  [VEHICLE_STATUS.TRIAGE]: "Triagem",
};

function getDeadlineText(
  daysAtBase: number,
  status: keyof typeof DEADLINE_STATUS
) {
  const limitDeadline = DEADLINE_STATUS[status];

  if (limitDeadline === undefined) {
    return "Prazo indefinido";
  }

  const difference = limitDeadline - daysAtBase;

  if (difference < 0) {
    if (Math.abs(difference) === 1) {
      return "Atrasado há 1 dia";
    }
    if (Math.abs(difference) > 1) {
      return `Atrasado há ${Math.abs(difference)} dias`;
    }
  } else if (difference === 0) {
    return "Prazo se encerra hoje";
  } else {
    if (Number.isNaN(difference)) {
      return "Alocada para Motoclub!";
    }

    if (difference === 1) {
      return "Falta 1 dia";
    }

    return `Faltam ${difference} dias`;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export function getStatusLabelsVehicle(status: VEHICLE_STATUS) {
  return STATUS_LABELS[status] || status;
}

const headers = {
  companyId: "Cidade",
  lastLogTimestamp: "Atualizado em",
  licensePlate: "Placa",
  location: "Link",
  status: "Status",
  expectedResolutionDate: "Data Prevista",
  daysAtBase: "Tempo na Base",
  days: "Prazo",
};

// const STATUS_ORDER = [
//   "Em manutenção",
//   "Reparo estético",
//   "Em preparação",
//   "Disponível"
// ];

function TableOperationalManagement() {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const situation = searchParams.get("situation");
  const treatment = searchParams.get("treatment");

  useContext(AuthContext);
  const companyId = getCompanyId() || "*";

  const { notifyError } = useToastContext();
  const [data, setData] = useState({
    itemsPerPage: 200,
    totalItems: 0,
    lastUpdated: new Date(),
  });
  const [dataOperationalManagement, setDataOperationalManagement] = useState<
    IOperationalManagement[]
  >([]);
  const [dataDashboard, setDataDashboard] =
    useState<IDashoardOperationalManagement>({
      total: 0,
      delayed: 0,
      ontime: 0,
      noteOperation: 0,
      totalVehicles: 0,
      totalRentedVehicles: 0,
    });
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    situation: situation || "",
    filterColumn: "",
    orderBy: "-daysAtBase",
    itemsPerPage: "200",
    treatment: treatment || "",
  });

  const company = useCompanyStore();

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      status: status
        ? status
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      situation: situation?.trim() ?? "",
      treatment: (typeof treatment === "boolean"
        ? String(treatment)
        : treatment ?? ""
      ).trim(),
    }));
  }, [status, situation, treatment]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        setDataOperationalManagement([]);

        const result = await getOperationalManagement({
          ...filters,
          page,
          itemsPerPage: Number(filters.itemsPerPage) || data.itemsPerPage,
        });

        if (result && !result.ok) {
          notifyError(result.message as string);
          return;
        }

        setData({
          itemsPerPage: data.itemsPerPage,
          totalItems: result.totalItems,
          lastUpdated: result.lastUpdated,
        });

        setDataOperationalManagement(result.data);
        setDataDashboard(result.dashboard as IDashoardOperationalManagement);
      } catch (error) {
        console.error("Error fetching operational management:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, companyId, data.itemsPerPage, page, company]);

  const isAction = (eventName: string) => action.eventName === eventName;
  const cleanActionData = () => setAction(EMPTY_ACTION);

  const actions = [
    {
      label: "Adicionar Observação",
      action: (data) => setAction({ eventName: "notes", data }),
      permission: "notes:operationalmanagement",
      disabled: (data) => data.status === VEHICLE_STATUS.AVAILABLE,
    },
    {
      label: "Trocar status",
      action: (data) => setAction({ eventName: "changeStatus", data }),
      permission: "status:vehicles",
    },
    {
      label: "Requisição de compra",
      action: (data) => setAction({ eventName: "purchaseRequisition", data }),
      permission: "status:vehicles",
    },
  ];

  const formatStyle = (
    content: React.ReactNode,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>
  ): React.ReactNode => {
    const shouldBlink = true;
    if (data.situation == "ONTIME") {
      return <span className="font-semibold">{content}</span>;
    } else {
      const className = shouldBlink
        ? "animate-pulse-text text-red-600 font-semibold"
        : "";
      return <span className={className}>{content}</span>;
    }
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  async function handlePublicLinkVelotrack(vehicleId: string) {
    try {
      const result = await savePublickLink(vehicleId);
      if (!result.ok) {
        notifyError("Veiculo não encontrado na Velotrack");
        return;
      }
    } catch (err) {
      console.log(err);
    }
  }

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Gestão Operacional"
          placeholder="Digite a placa"
          statusOptions={STATUS_LABELS}
          onNewItemClick={() =>
            setAction({ eventName: "edit", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="operationalmanagement"
          tableData={dataOperationalManagement}
          customOrderBy="-daysAtBase"
        />
        {!isLoading && (
          <div className="grid grid-cols-2 gap-3 mt-1 mb-3 lg:grid-cols-4">
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
              <h2 className="text-sm text-white uppercase w-1/2">Total</h2>
              <p className="text-lg text-white w-1/2 text-right">
                {dataDashboard.total}
              </p>
            </div>
            <div
              className={`bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition ${
                dataDashboard.delayed > 0 ? "animate-pulse-color" : ""
              }`}
            >
              <h2 className="text-sm text-white uppercase w-1/2">Atrasado</h2>
              <p className="text-lg text-white w-1/2 text-right">
                {dataDashboard.delayed}
              </p>
            </div>
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
              <h2 className="text-sm text-white uppercase w-1/2">No Prazo</h2>
              <p className="text-lg text-white w-1/2 text-right">
                {dataDashboard.ontime}
              </p>
            </div>
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-2 flex items-center hover:shadow-lg transition">
              <Popover
                content={
                  <div className="text-black border border-gray-300 rounded-md shadow-md p-2 max-w-sm relative z-40">
                    <span className="text-sm">{`(Total de Veículos Locados: ${dataDashboard.totalRentedVehicles} / Total de Veículos: ${dataDashboard.totalVehicles}) * 10`}</span>
                  </div>
                }
                placement="top"
                trigger="click"
              >
                <div
                  className="cursor-pointer text-blue-600"
                  role="button-operational-management-group"
                >
                  <FaInfoCircle className="text-white" />
                </div>
              </Popover>
              <h2 className="text-sm text-white uppercase w-1/2 ml-2">
                Nota da Operação
              </h2>
              <p className="text-lg text-white w-1/2 text-right">
                {dataDashboard.noteOperation}
              </p>
            </div>
          </div>
        )}

        <GenericTable
          headers={headers}
          // data={[...dataOperationalManagement].sort((a, b) => {
          //   const isDelayedA = a.situation !== 'ONTIME';
          //   const isDelayedB = b.situation !== 'ONTIME';

          //   if (isDelayedA !== isDelayedB) {
          //     return isDelayedA ? -1 : 1;
          //   }

          //   const labelA = getStatusLabelsVehicle(a.status);
          //   const labelB = getStatusLabelsVehicle(b.status);

          //   const indexA = STATUS_ORDER.indexOf(labelA);
          //   const indexB = STATUS_ORDER.indexOf(labelB);

          //   const safeIndexA = indexA === -1 ? STATUS_ORDER.length : indexA;
          //   const safeIndexB = indexB === -1 ? STATUS_ORDER.length : indexB;

          //   return safeIndexA < safeIndexB ? -1 : 1;
          // })}
          data={dataOperationalManagement}
          actions={actions.map((action) => ({
            ...action,
            disabled: action.disabled ? action.disabled : () => false,
          }))}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              companyId: (companyId, data) => {
                return formatStyle(getCompanySigla(companyId) || "", data);
              },
              licensePlate: (licensePlate, data) => {
                const hasNotes = data.notes && data.notes.length > 0;

                return formatStyle(
                  <div className="inline-flex items-center gap-[4px]">
                    {hasNotes ? (
                      <button
                        title="Visualizar observações"
                        className="inline-flex items-center gap-[4px]  hover:text-red-800 p-0 m-0 leading-none font-semibold text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAction({ eventName: "view-notes", data });
                        }}
                      >
                        <span>{licensePlate}</span>
                        <FaInfoCircle
                          size={12}
                          className="relative top-[1px]"
                        />
                      </button>
                    ) : (
                      <span className="font-semibold text-sm text-red">
                        {licensePlate}
                      </span>
                    )}
                  </div>,
                  data
                );
              },
              status: (status, data) => {
                return formatStyle(getStatusLabelsVehicle(status) || "", data);
              },
              expectedResolutionDate: (_expectedDate, data) => {
                if (!data || !data.notes || data.notes.length === 0) {
                  return formatStyle("Não Informada", data);
                }

                const mostRecentNote = data.notes
                  .filter((note) => note.expectedResolutionDate)
                  .sort(
                    (a, b) =>
                      new Date(b.expectedResolutionDate).getTime() -
                      new Date(a.expectedResolutionDate).getTime()
                  )[0];

                const dateToDisplay =
                  mostRecentNote?.expectedResolutionDate || null;

                return formatStyle(
                  dateToDisplay
                    ? dateToDisplay.slice(0, 10).split("-").reverse().join("/")
                    : "Não Informada",
                  data
                );
              },
              days: (_, data) => {
                return formatStyle(
                  getDeadlineText(data.daysAtBase, data.status),
                  data
                );
              },
              daysAtBase: (daysAtBase, data) => {
                return (
                  <span
                    onClick={() =>
                      setAction({ eventName: "infoDaysAtBase", data })
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {formatStyle(daysAtBase, data)}
                  </span>
                );
              },
              lastLogTimestamp: (lastlogtimestamp) => {
                return (
                  <span>
                    {new Date(lastlogtimestamp as string).toLocaleString()}
                  </span>
                );
              },
              location: (_, data) => {
                return formatStyle(
                  data.vehicleLatitude && data.vehicleLongitude ? (
                    <a
                      href={`https://www.google.com/maps?q=${data.vehicleLatitude},${data.vehicleLongitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline cursor-pointer"
                    >
                      Link
                    </a>
                  ) : (
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePublicLinkVelotrack(data.vehicleId);
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline cursor-pointer"
                    >
                      Link
                    </a>
                  ),
                  data
                );
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="operationalmanagement"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 200)}
          goToPage={setPage}
        />

        {isAction("purchaseRequisition") && (
          <ModalCreatePurchaseRequisition
            closeModal={cleanActionData}
            licensePlate={(action.data as IOperationalManagement).licensePlate}
          />
        )}
        {isAction("notes") && (
          <ModalAddNotesOperationalManagement
            operationalManagement={action.data as IOperationalManagement}
            onClose={cleanActionData}
          />
        )}
        {isAction("view-notes") && (
          <ModalViewNotesOperationalManagement
            operationalManagement={action.data as IOperationalManagement}
            onClose={cleanActionData}
          />
        )}
        {isAction("changeStatus") && (
          <ModalChangeStatusVehicle
            vehicle={{
              licensePlate: (action.data as IOperationalManagement)
                .licensePlate,
              status: (action.data as IOperationalManagement).status,
            }}
            onClose={cleanActionData}
          />
        )}
        {isAction("infoDaysAtBase") && (
          <ModalInfoDaysAtBase
            vehicle={{
              licensePlate: (action.data as IOperationalManagement)
                .licensePlate,
            }}
            onClose={cleanActionData}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableOperationalManagement;
